import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import MainLayout from "../Common/MainLayout";
import CriteriaList from "./CriteriaList";
import CriteriaDetails from "./CriteriaDetails";
import "./CriteriaManagement.css";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import httpService from "../../services/httpService";
import { fetchAuthSession } from "aws-amplify/auth";
import config from "../../config";

const CriteriaManagement = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isInvalid, setIsInvalid] = useState(false);
  const [selectedCriteriaType, setSelectedCriteriaType] = useState(0);
  const [selectedCriteria, setSelectedCriteria] = useState({
    criteriaId: null,
    criteriaName: null,
  });
  const [minimumProviders, setMinimumProviders] = useState([]);
  const [criteriaTypes, setCriteriaTypes] = useState([]);
  const toast = useRef(null);

  useEffect(() => {
    fetchCriteriaTypes();
  }, []);

  const fetchCriteriaTypes = async () => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "GetCriteriaTypes",
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        const criteriaTypeData = response.data.map((item) => ({
          name: item.criteriaTypeName,
          code: item.criteriaTypeId,
        }));
        setCriteriaTypes(criteriaTypeData);
      })
      .catch((error) => {
        console.error("Error fetching criteria types:", error);
      });
  };

  const fetchCriteriaList = async () => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "MinimumProvider",
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        const minimumProvidersData = response.data.map((item) => ({
          criteriaName: item.criteria_name,
          criteriaId: item.criteria_id,
        }));
        setMinimumProviders(minimumProvidersData);

        if (minimumProvidersData.length > 0) {
          setSelectedCriteria({
            criteriaId: minimumProvidersData[0].criteriaId,
            criteriaName: minimumProvidersData[0].criteriaName,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching minimum providers:", error);
      });
  };

  useEffect(() => {
    fetchCriteriaList();
  }, []);

  const handleCriteriaSelect = (criteriaId, criteriaName) => {
    setSelectedCriteria({ criteriaId, criteriaName });
  };

  const handleCriteriaDeleted = () => {
    fetchCriteriaList();
  };

  const handleOpen = () => {
    setOpen(true);
    setIsInvalid(false);
  };

  const handleClose = () => {
    setInputValue("");
    setIsInvalid(false);
    setOpen(false);
  };

  const handleSave = async () => {
    if (inputValue.trim() === "") {
      setIsInvalid(true);
      return;
    }
    if (selectedCriteriaType <= 0) {
      setIsInvalid(true);
      return;
    }

    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    console.log("Criteria Name: ", inputValue);
    console.log("selectedCriteriaType: ", selectedCriteriaType.code);
    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "CreateCriteria",
          nam_criteria: inputValue,
          id_net_adq_criteria_type: selectedCriteriaType.code,
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        handleClose();
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Criteria has been created successfully.",
          life: 3000,
        });
        fetchCriteriaList();
      })
      .catch((error) => {
        toast.current.show({
          severity: "Error",
          summary: "Failed",
          detail: "Error creating criteria. Please try again.",
          life: 3000,
        });
        handleClose();
      });
  };

  return (
    <MainLayout>
      <Toast ref={toast} position="top-right" />
      <div className="criteriamanagement-container">
        <CriteriaList
          onCriteriaSelect={handleCriteriaSelect}
          criteriaList={minimumProviders}
          selectedCriteriaId={selectedCriteria.criteriaId}
        />
        <div className="criteriamanagement-content">
          <div className="criteria-details">
            {config.FEATURE_FLAGS.CRITERIA_MANAGEMENT.createCriteria && (
              <span style={{ display: "flex", justifyContent: "right" }}>
                <Button
                  label="Create"
                  icon="pi pi-plus"
                  className="p-button-primary"
                  size="small"
                  style={{ margin: "20px 0px 10px 0", width: "100px" }}
                  onClick={() => handleOpen()}
                />
              </span>
            )}
            <CriteriaDetails
              criteriaId={selectedCriteria.criteriaId}
              criteriaName={selectedCriteria.criteriaName}
              onCriteriaDeleted={handleCriteriaDeleted}
            />
          </div>
        </div>
        <AddCriteriaDialog
          open={open}
          inputValue={inputValue}
          setInputValue={setInputValue}
          handleClose={handleClose}
          handleSave={handleSave}
          isInvalid={isInvalid}
          selectedCriteriaType={selectedCriteriaType}
          setSelectedCriteriaType={setSelectedCriteriaType}
          criteriaTypes={criteriaTypes}
        />
      </div>
    </MainLayout>
  );
};

const AddCriteriaDialog = ({
  open,
  inputValue,
  setInputValue,
  handleClose,
  handleSave,
  isInvalid,
  selectedCriteriaType,
  setSelectedCriteriaType,
  criteriaTypes,
}) => {
  return (
    <Dialog
      header="ADD NEW CRITERIA"
      visible={open}
      style={{ width: "50vw" }}
      onHide={handleClose}
      footer={
        <div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={handleClose}
          />
          <Button label="Save" icon="pi pi-check" onClick={handleSave} />
        </div>
      }
    >
      <div>
        <div style={{ textAlign: "center" }}>
          <label
            htmlFor="criteriaName"
            style={{
              display: "block",
              textAlign: "left",
              paddingLeft: "40px",
              marginBottom: "8px",
            }}
          >
            Criteria Name
          </label>
          <InputText
            id="criteriaName"
            type="text"
            value={inputValue}
            placeholder="Enter Criteria Name"
            onChange={(e) => setInputValue(e.target.value)}
            style={{
              width: "90%",
              padding: "12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
            className={isInvalid ? "p-invalid" : ""}
            required
          />
        </div>
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <label
            htmlFor="criteriaType"
            style={{
              display: "block",
              textAlign: "left",
              paddingLeft: "40px",
              marginBottom: "8px",
            }}
          >
            Criteria Type
          </label>
          <Dropdown
            id="criteriaType"
            value={selectedCriteriaType}
            onChange={(e) => setSelectedCriteriaType(e.value)}
            options={criteriaTypes}
            optionLabel="name"
            placeholder="Select Criteria Type"
            style={{
              width: "90%",
              border: "1px solid #ccc",
              borderRadius: "4px",
              textAlign: "left",
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CriteriaManagement;
